import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { format } from "date-fns";
import { getDateLocale } from "@/lib/i18n/formatLocale";
import { currencyService } from "@/lib/services/currency.service";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formate une date selon la langue (fr → format français, en → en-US).
 * @param dateString - Date en string ou ISO
 * @param lang - Code langue (défaut "fr"). Passer i18n.language dans les composants.
 */
export function formatDate(dateString: string, lang?: string): string {
  const locale = getDateLocale(lang ?? "fr");
  try {
    return format(new Date(dateString), 'PPP', { locale });
  } catch {
    return dateString;
  }
}

/**
 * Formate un prix en MAD avec séparateur de milliers selon la langue.
 */
export function formatPrice(amount: number, lang?: string): string {
  const selectedCurrency = currencyService.getStoredCurrency();
  return currencyService.formatPriceFromMad(amount, selectedCurrency, lang ?? "fr");
}

export function formatCurrency(amount: number, lang?: string): string {
  const selectedCurrency = currencyService.getStoredCurrency();
  return currencyService.formatPriceFromMad(amount, selectedCurrency, lang ?? "fr", undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Formats an amount strictly in MAD (gateway charge currency).
 * Use for billing disclosures alongside display-currency labels — not as the primary CTA label when the user selected another currency.
 */
export function formatMadCurrency(amount: number, lang?: string): string {
  return currencyService.formatPriceFromMad(amount, "MAD", lang ?? "fr", undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export const SERVICE_FEE_RATE = 0;

/**
 * Returns the daily price for display on listings, cards, detail pages.
 * Service fee is currently disabled (0) — commission handled internally by payout engine.
 * Rounds to the nearest integer for clean display.
 */
export function getDisplayPricePerDay(basePricePerDay: number): number {
  return Math.round(basePricePerDay * (1 + SERVICE_FEE_RATE));
}

/**
 * Obtient le nom d'affichage d'un profil selon son rôle
 * Pour les owners, utilise agency_name
 * Pour les renters, utilise first_name + last_name
 *
 * @param profile - Le profil avec role, agency_name, first_name, last_name
 * @returns Le nom d'affichage
 */
export function getDisplayName(profile: {
  role?: 'owner' | 'renter' | 'admin' | null;
  agency_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}): string {
  if (profile.role === 'owner' && profile.agency_name) {
    return profile.agency_name;
  }
  const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
  return fullName || 'Utilisateur';
}

/**
 * Obtient les initiales d'un profil pour l'avatar
 * Pour les owners avec agency_name, prend les 2 premiers caractères
 * Pour les renters, prend la première lettre de first_name et last_name
 *
 * @param profile - Le profil avec role, agency_name, first_name, last_name
 * @returns Les initiales (2 caractères)
 */
export function getInitials(profile: {
  role?: 'owner' | 'renter' | 'admin' | null;
  agency_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}): string {
  if (profile.role === 'owner' && profile.agency_name) {
    return profile.agency_name.substring(0, 2).toUpperCase();
  }
  const first = (profile.first_name?.[0] || '').toUpperCase();
  const last = (profile.last_name?.[0] || '').toUpperCase();
  return (first + last) || 'U';
}

/**
 * Vérifie si le bucket 'vehicles' est accessible publiquement
 * @returns Promise<boolean> - true si accessible, false sinon
 */
export async function checkBucketAccessibility(): Promise<boolean> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      console.warn('⚠️ VITE_SUPABASE_URL not configured');
      return false;
    }
    // Tester avec un chemin de test (cette requête échouera mais nous indiquera si le bucket est public)
    const testUrl = `${supabaseUrl}/storage/v1/object/public/vehicles/test-check.txt`;

    await fetch(testUrl, {
      method: 'HEAD',
      mode: 'no-cors'
    });

    // En mode no-cors, on ne peut pas lire le status, mais si ça ne lance pas d'erreur,
    // c'est un bon signe (même si le fichier n'existe pas)
    return true;
  } catch {
    if (import.meta.env.DEV) {
    console.warn('⚠️ Impossible de vérifier l\'accessibilité du bucket vehicles');
    }
    return false;
  }
}

import { getVehicleImageUrlCore, VEHICLE_IMAGE_PLACEHOLDER } from "shared/utils/vehicle-image-url";

export type { GetVehicleImageUrlOptions } from "shared/utils/vehicle-image-url";

/** URL du placeholder résolue avec BASE_URL pour éviter 404 en déploiement (sous-chemin ou CDN). */
export function getPlaceholderUrl(): string {
  const base = (import.meta.env.BASE_URL || "/").replace(/\/+$/, "") || "/";
  return `${base}${base.endsWith("/") ? "" : "/"}placeholder.svg`;
}

/**
 * Formate une URL d'image depuis Supabase Storage (avec option de transform pour listes/détail).
 * Supporte : URLs complètes Supabase, chemins (vehicleId/file.jpg), bucket legacy car-images.
 * Pour CDN futur : changer la base URL dans un seul endroit (env par app).
 *
 * @param imageUrl - URL ou chemin de l'image
 * @param options - Optionnel : width, height, quality pour Supabase Image Transform (Pro)
 * @returns URL complète ou transformée, ou placeholder si invalide
 */
export function getVehicleImageUrl(
  imageUrl: string | undefined | null,
  options?: { width?: number; height?: number; quality?: number }
): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl || (typeof supabaseUrl === "string" && supabaseUrl.includes("placeholder"))) {
    if (import.meta.env.DEV) {
      console.warn('VITE_SUPABASE_URL not set, vehicle images may fail');
    }
    return getPlaceholderUrl();
  }
  const url = getVehicleImageUrlCore(imageUrl, options, supabaseUrl.replace(/\/$/, ''));
  return url === VEHICLE_IMAGE_PLACEHOLDER ? getPlaceholderUrl() : url;
}

/**
 * Formate un tableau d'URLs d'images depuis Supabase Storage (optionnel avec transform).
 *
 * @param images - Tableau d'URLs ou chemins d'images
 * @param options - Optionnel : width, height, quality pour détail/galerie
 * @returns Tableau d'URLs complètes (filtré des valeurs invalides)
 */
export function getVehicleImagesUrls(
  images: string[] | undefined | null,
  options?: { width?: number; height?: number; quality?: number }
): string[] {
  if (!images || images.length === 0) {
    return [getPlaceholderUrl()];
  }

  const validImages = images
    .map(img => getVehicleImageUrl(img, options))
    .filter(url => url !== getPlaceholderUrl());

  if (validImages.length === 0) {
    return [getPlaceholderUrl()];
  }

  return validImages;
}

/**
 * Magic bytes (file signatures) pour détecter le type réel d'un fichier
 * Plus fiable que l'extension ou file.type
 */
const FILE_SIGNATURES: Record<string, number[][]> = {
  'image/jpeg': [[0xFF, 0xD8, 0xFF]],
  'image/png': [[0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A]],
  'application/pdf': [[0x25, 0x50, 0x44, 0x46]], // %PDF
};

/**
 * MIME type mapping basé sur l'extension
 */
const MIME_TYPE_MAP: Record<string, string> = {
  'jpg': 'image/jpeg',
  'jpeg': 'image/jpeg',
  'png': 'image/png',
  'pdf': 'application/pdf',
  'webp': 'image/webp',
  'heic': 'image/heic',
};

/**
 * Types MIME autorisés pour les documents
 */
export const ALLOWED_DOCUMENT_TYPES = ['image/jpeg', 'image/png', 'application/pdf'] as const;

/**
 * Types MIME autorisés pour les images
 */
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'] as const;

/**
 * Détecte le type MIME réel d'un fichier en analysant ses magic bytes
 * @param arrayBuffer - Le contenu du fichier
 * @returns Le type MIME détecté ou null si non reconnu
 */
function detectMimeTypeFromContent(arrayBuffer: ArrayBuffer): string | null {
  const bytes = new Uint8Array(arrayBuffer);

  for (const [mimeType, signatures] of Object.entries(FILE_SIGNATURES)) {
    for (const signature of signatures) {
      if (bytes.length >= signature.length) {
        const matches = signature.every((byte, index) => bytes[index] === byte);
        if (matches) {
          return mimeType;
        }
      }
    }
  }

  return null;
}

/**
 * Normalise un fichier en déterminant le MIME type correct.
 * Utilise une approche en 3 niveaux :
 * 1. Magic bytes (le plus fiable)
 * 2. Extension du fichier
 * 3. file.type (si valide)
 *
 * @param file - Le fichier original à normaliser
 * @param allowedTypes - Types MIME autorisés (optionnel, pour validation)
 * @returns Un nouveau File avec le MIME type correct
 */
export async function normalizeFileMimeType(
  file: File,
  allowedTypes?: string[]
): Promise<File> {
  const fileExt = file.name.split('.').pop()?.toLowerCase() || '';

  // Étape 1: Lire le contenu pour détecter les magic bytes
  const arrayBuffer = await file.arrayBuffer();
  const detectedType = detectMimeTypeFromContent(arrayBuffer);

  // Étape 2: Déterminer le MIME type correct
  let contentType: string;

  if (detectedType) {
    // Priorité au type détecté depuis le contenu
    contentType = detectedType;
    if (import.meta.env.DEV) {
      console.log('✅ MIME type détecté depuis le contenu:', contentType, 'pour', file.name);
    }
  } else if (fileExt && MIME_TYPE_MAP[fileExt]) {
    // Fallback sur l'extension
    contentType = MIME_TYPE_MAP[fileExt];
    if (import.meta.env.DEV) {
      console.log('✅ MIME type déterminé depuis l\'extension:', contentType, 'pour', file.name, '(file.type était:', file.type, ')');
    }
  } else if (file.type &&
             file.type !== 'application/json' &&
             file.type !== 'application/octet-stream' &&
             (file.type.startsWith('image/') || file.type === 'application/pdf')) {
    // Utiliser file.type si valide
    contentType = file.type;
    if (import.meta.env.DEV) {
      console.log('✅ MIME type depuis file.type:', contentType, 'pour', file.name);
    }
  } else {
    // Dernier recours : fallback basé sur l'extension
    if (fileExt === 'pdf') {
      contentType = 'application/pdf';
    } else if (fileExt === 'png') {
      contentType = 'image/png';
    } else {
      contentType = 'image/jpeg'; // Default fallback
    }
    if (import.meta.env.DEV) {
      console.warn('⚠️ MIME type non détecté, utilisation du fallback:', contentType, 'pour', file.name, '(extension:', fileExt, ', file.type:', file.type, ')');
    }
  }

  // Validation si des types autorisés sont spécifiés
  if (allowedTypes && !allowedTypes.includes(contentType)) {
    throw new Error(
      `Type de fichier non autorisé: ${contentType}. Types autorisés: ${allowedTypes.join(', ')}`
    );
  }

  // Si le type est déjà correct, retourner le fichier tel quel
  if (file.type === contentType) {
    return file;
  }

  // Créer un nouveau Blob avec le bon type
  const blob = new Blob([arrayBuffer], { type: contentType });

  // Créer un nouveau File avec le Blob corrigé
  const normalizedFile = new File([blob], file.name, {
    type: contentType,
    lastModified: file.lastModified || Date.now()
  });

  return normalizedFile;
}

/**
 * Options pour l'upload de fichiers
 */
export interface UploadFileOptions {
  /** Bucket Supabase Storage */
  bucket: string;
  /** Chemin du fichier dans le bucket */
  path: string;
  /** Types MIME autorisés (optionnel) */
  allowedTypes?: string[];
  /** Taille maximale en bytes (optionnel) */
  maxSize?: number;
  /** Remplacer le fichier s'il existe déjà */
  upsert?: boolean;
}

/**
 * Résultat d'un upload réussi
 */
export interface UploadResult {
  /** Chemin du fichier uploadé */
  path: string;
  /** URL publique du fichier (si le bucket est public) */
  publicUrl?: string;
}

/**
 * Wrapper centralisé pour uploader des fichiers vers Supabase Storage.
 * Applique automatiquement la normalisation du MIME type et la validation.
 *
 * Principe SOLID : Single Responsibility - cette fonction a une seule responsabilité : uploader avec normalisation
 *
 * @param file - Le fichier à uploader
 * @param options - Options d'upload
 * @returns Le résultat de l'upload
 * @throws Error si l'upload échoue ou si le fichier n'est pas valide
 */
export async function uploadToStorage(
  file: File,
  options: UploadFileOptions
): Promise<UploadResult> {
  const { supabase } = await import('@/lib/supabase');

  // Validation de la taille
  if (options.maxSize && file.size > options.maxSize) {
    throw new Error(
      `Fichier trop volumineux: ${(file.size / 1024 / 1024).toFixed(2)}MB. Maximum: ${(options.maxSize / 1024 / 1024).toFixed(2)}MB`
    );
  }

  // Normalisation du MIME type
  const normalizedFile = await normalizeFileMimeType(file, options.allowedTypes);

  // Upload vers Supabase Storage
  const { data: uploadData, error: uploadError } = await supabase.storage
    .from(options.bucket)
    .upload(options.path, normalizedFile, {
      contentType: normalizedFile.type,
      upsert: options.upsert ?? false,
    });

  if (uploadError) {
    // Log détaillé en développement
    if (import.meta.env.DEV) {
      console.error('❌ Erreur upload:', {
        error: uploadError,
        bucket: options.bucket,
        path: options.path,
        fileType: normalizedFile.type,
        fileName: file.name,
        fileSize: file.size,
      });
    }
    throw new Error(uploadError.message);
  }

  // Obtenir l'URL publique si le bucket est public
  let publicUrl: string | undefined;
  try {
    const { data: urlData } = supabase.storage
      .from(options.bucket)
      .getPublicUrl(uploadData.path);
    publicUrl = urlData.publicUrl;
  } catch {
    // Le bucket n'est peut-être pas public, ce n'est pas une erreur
  }

  return {
    path: uploadData.path,
    publicUrl,
  };
}
